import type { EditableCard, VisualOverride } from '@/frontend/features/DashboardRuntime/types'

const SERIES = ['bar', 'line', 'area', 'stacked_bar']

// A card can change in a saved view when it can be redrawn (more than one type) or restyled.
export function canChangeAppearance(card: EditableCard): boolean {
  const editing = card.editing
  return !!editing && (editing.chart_types.length > 1 || editing.cartesian)
}

export default function ViewVisualEditor({ card, value, onChange, copy }: {
  card: EditableCard; value: VisualOverride; onChange: (value: VisualOverride) => void; copy: Record<string, string>
}) {
  const capabilities = card.editing
  if (!capabilities || !canChangeAppearance(card)) return null
  const defaults = capabilities.defaults ?? {}
  const chart = value.chart ?? defaults.chart ?? capabilities.chart_types[0] ?? ''
  // Color and axis titles belong to a redrawn chart; the card's own visual and its table keep theirs.
  const styled = capabilities.cartesian && SERIES.includes(chart)
  const orientable = (capabilities.orientable ?? capabilities.cartesian) && (chart === 'bar' || chart === 'stacked_bar')
  return <div className="grid grid-cols-2 gap-3">
    {capabilities.chart_types.length > 1 && <label className="col-span-2 space-y-1 text-xs">{copy.chart_type}<select aria-label={`${copy.chart_type}: ${card.title}`} className="w-full rounded-md border bg-background p-2" value={chart}
      onChange={event => onChange({ ...value, chart: event.target.value })}>
      {capabilities.chart_types.map(type => <option key={type} value={type}>{copy[`chart_${type}`] ?? type}</option>)}
    </select></label>}
    {styled && <label className="space-y-1 text-xs">{copy.color}<input type="color" aria-label={`${copy.color}: ${card.title}`} className="h-9 w-full rounded-md border bg-background p-1" value={value.color ?? '#7047eb'}
      onChange={event => onChange({ ...value, color: event.target.value })} /></label>}
    {orientable && <label className="space-y-1 text-xs">{copy.orientation}<select aria-label={`${copy.orientation}: ${card.title}`} className="w-full rounded-md border bg-background p-2" value={value.orientation ?? defaults.orientation ?? 'auto'}
      onChange={event => onChange({ ...value, orientation: event.target.value as VisualOverride['orientation'] })}>
      {['auto', 'vertical', 'horizontal'].map(item => <option key={item} value={item}>{copy[item]}</option>)}
    </select></label>}
    {styled && (['x_axis_label', 'y_axis_label'] as const).map(key => <label key={key} className="space-y-1 text-xs">{copy[key]}
      <input maxLength={100} aria-label={`${copy[key]}: ${card.title}`} className="w-full rounded-md border bg-background p-2" value={value[key] ?? defaults[key] ?? ''}
        onChange={event => onChange({ ...value, [key]: event.target.value })} />
    </label>)}
    <button type="button" className="col-span-2 justify-self-start rounded-md border px-3 py-2 text-xs" onClick={() => onChange({})}>{copy.reset_visual}</button>
  </div>
}
